import React, { useState, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { getCategories } from '../../data/storeData';
import { trackCategoryFilter } from '../../utils/analytics';

const CategoryFilter = ({ activeCategory, setActiveCategory }) => {
  const [categories, setCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats || []);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();

    const handleUpdate = () => loadCategories();
    window.addEventListener('categoriesUpdated', handleUpdate);

    return () => {
      window.removeEventListener('categoriesUpdated', handleUpdate);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setExpandedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (categoryId, e) => {
    e.stopPropagation();
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    setExpandedCategory(null);
    trackCategoryFilter(categoryId);
  };

  const handleSubcategoryClick = (subcategoryId) => {
    setActiveCategory(subcategoryId);
    setExpandedCategory(null);
  };

  const activeName =
    categories.find((c) => c.id === activeCategory)?.name ||
    categories.flatMap((c) => c.subcategories || []).find((s) => s.id === activeCategory)?.name ||
    null;

  if (loading) {
    return (
      <div className="sticky top-[68px] z-30 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 py-5">
        <div className="container mx-auto px-6 flex justify-center gap-3">
          {[80, 100, 90, 110].map((w, i) => (
            <div
              key={i}
              className="h-10 rounded-full bg-gray-100 animate-pulse"
              style={{ width: `${w}px` }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-[68px] z-30 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
      <div className="container mx-auto px-4 md:px-6 py-3.5">
        {/* Scrollable pill row */}
        <div className="flex items-center gap-2.5 overflow-x-auto no-scrollbar md:flex-wrap md:justify-center snap-x px-2 -mx-2">
          {/* All Products */}
          <button
            onClick={() => handleCategoryClick('all')}
            className={`flex-shrink-0 snap-start px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 active:scale-95 ${
              activeCategory === 'all'
                ? 'bg-[#1d1d1f] text-white shadow-[0_8px_20px_-6px_rgba(0,0,0,0.4)]'
                : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 hover:text-gray-900'
            }`}
          >
            All Products
          </button>

          {/* Category pills with dropdowns */}
          {categories.map((category) => {
            const hasSubcategories = category.subcategories && category.subcategories.length > 0;
            const isExpanded = expandedCategory === category.id;
            const isActive =
              activeCategory === category.id ||
              category.subcategories?.some((sub) => sub.id === activeCategory);

            return (
              <div key={category.id} className="relative dropdown-container flex-shrink-0 snap-start">
                <button
                  onClick={() => handleCategoryClick(category.id)}
                  className={`flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 active:scale-95 ${
                    isActive
                      ? 'bg-[#1d1d1f] text-white shadow-[0_8px_20px_-6px_rgba(0,0,0,0.4)]'
                      : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 hover:text-gray-900'
                  }`}
                >
                  <span>{category.name}</span>
                  {hasSubcategories && (
                    <span
                      onClick={(e) => toggleDropdown(category.id, e)}
                      className="-mr-1.5 p-0.5 rounded-full hover:bg-white/20"
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </span>
                  )}
                </button>

                {/* Subcategory dropdown */}
                {hasSubcategories && isExpanded && (
                  <div
                    className="absolute top-full left-1/2 mt-3 bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] border border-gray-100 overflow-hidden z-50 min-w-[210px]"
                    style={{ transform: 'translateX(-50%)', animation: 'dropInCentered 0.25s cubic-bezier(0.16,1,0.3,1)' }}
                  >
                    <div className="p-1.5">
                      {category.subcategories.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => handleSubcategoryClick(sub.id)}
                          className={`w-full px-4 py-2.5 text-left text-sm rounded-xl transition-colors duration-200 ${
                            activeCategory === sub.id
                              ? 'bg-[#1d1d1f] text-white font-semibold'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Active filter chip */}
        {activeCategory !== 'all' && activeName && (
          <div className="mt-3 flex justify-center animate-[dropIn_0.25s_ease-out]">
            <button
              onClick={() => handleCategoryClick('all')}
              className="group inline-flex items-center gap-2 pl-4 pr-2.5 py-1.5 bg-orange-50 text-orange-600 border border-orange-200/60 rounded-full text-xs font-semibold hover:bg-orange-100 transition-colors"
            >
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              {activeName}
              <span className="p-0.5 rounded-full bg-orange-100 group-hover:bg-orange-200 transition-colors">
                <X className="w-3 h-3" />
              </span>
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dropInCentered {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CategoryFilter;
